import type {
  UnitType,
  UsabilityTier,
} from '../lib/types.js';
import { unitVolumeFactor } from '../lib/unit-volume-factor.js';
import type { SustainabilityTier } from '../unit-meta/index.js';
import {
  isCorsiaActual,
  isCorsiaDefault,
  isEuActual,
  isEuDefault,
  type IssueMetadata,
} from '../unit-meta/index.js';
import { CalculationsV1CorsiaActual } from './calculations-v1/corsiaActual.js';
import { CalculationsV1CorsiaDefault } from './calculations-v1/corsiaDefault.js';
import { CalculationsV1EuActual } from './calculations-v1/euActual.js';
import { CalculationsV1EuDefault } from './calculations-v1/euDefault.js';
import { CalculationsV1UsabilityTier } from './calculations-v1/usability-tier.js';
import { calculationsCorsia } from './calculations-v2-03.2026/corsia.js';
import { calculationsEu } from './calculations-v2-03.2026/eu.js';
import {
  SustainabilityTierTypes,
  UsabilityTierInfoForSAFC,
} from './constants.js';

const isV1 = (metadata: IssueMetadata) => metadata.batch.additionalityApproach === undefined;

export const calculateUsabilityTier = (
  metadata: IssueMetadata,
  params: {
    unitType: UnitType;
    sustainabilityTier: SustainabilityTier;
    hasLinkedTree?: boolean;
    isParentTreeClaimed?: boolean;
  },
): UsabilityTier => {
  if (isV1(metadata)) {
    return CalculationsV1UsabilityTier.calculateUsabilityTier(metadata, params.unitType, params.hasLinkedTree, params.isParentTreeClaimed);
  }

  if (isEuDefault(metadata)) {
    return calculationsEu.default.usabilityTier(metadata, params);
  } else if (isEuActual(metadata)) {
    return calculationsEu.actual.usabilityTier(metadata, params);
  } else if (isCorsiaActual(metadata)) {
    return calculationsCorsia.actual.usabilityTier(metadata, params);
  } else if (isCorsiaDefault(metadata)) {
    return calculationsCorsia.default.usabilityTier(metadata, params);
  } else {
    const _: never = metadata;
    throw new Error(`Unknown certification: ${JSON.stringify((metadata as IssueMetadata).certification)}`);
  }
};

export const calculateSustainabilityTier = (
  metadata: IssueMetadata,
): SustainabilityTier => {
  if (metadata['sustainabilityTier']) {
    return metadata['sustainabilityTier'];
  }

  if (isV1(metadata)) {
    if (isEuDefault(metadata)) {
      return CalculationsV1EuDefault.calculateSustainabilityTierForEUDefault(metadata);
    } else if (isEuActual(metadata)) {
      return CalculationsV1EuActual.calculateSustainabilityTierForEUActual(metadata);
    } else if (isCorsiaActual(metadata)) {
      return CalculationsV1CorsiaActual.calculateSustainabilityTierForCorsiaActual(metadata);
    } else if (isCorsiaDefault(metadata)) {
      return CalculationsV1CorsiaDefault.calculateSustainabilityTierForCorsiaDefault(metadata);
    } else {
      const _: never = metadata;
      throw new Error(`Unknown certification: ${JSON.stringify((metadata as IssueMetadata).certification)}`);
    }
  }

  if (isEuDefault(metadata)) {
    return calculationsEu.default.sustainabilityTier(metadata);
  } else if (isEuActual(metadata)) {
    return calculationsEu.actual.sustainabilityTier(metadata);
  } else if (isCorsiaActual(metadata)) {
    return calculationsCorsia.actual.sustainabilityTier(metadata);
  } else if (isCorsiaDefault(metadata)) {
    return calculationsCorsia.default.sustainabilityTier(metadata);
  } else {
    const _: never = metadata;
    throw new Error(`Unknown certification: ${JSON.stringify((metadata as IssueMetadata).certification)}`);
  }
};

export const calculateCarbonIntensityScore = (
  metadata: IssueMetadata,
  { sustainabilityTier }: { sustainabilityTier: SustainabilityTier },
): number => {
  if (metadata.adjustedLCAValue !== undefined && Number(metadata.adjustedLCAValue) === 0) {
    return 0;
  }

  if (isV1(metadata)) {
    if (isCorsiaDefault(metadata)) {
      return parseFloat(CalculationsV1CorsiaDefault.equations[sustainabilityTier](metadata).toFixed(unitVolumeFactor));
    } else if (isCorsiaActual(metadata)) {
      return parseFloat(CalculationsV1CorsiaActual.equations[sustainabilityTier](metadata).toFixed(unitVolumeFactor));
    } else if (isEuActual(metadata)) {
      return parseFloat(CalculationsV1EuActual.equations[sustainabilityTier](metadata).toFixed(unitVolumeFactor));
    } else if (isEuDefault(metadata)) {
      return parseFloat(CalculationsV1EuDefault.equations[sustainabilityTier](metadata).toFixed(unitVolumeFactor));
    } else {
      const _: never = metadata;
      throw new Error(`Unknown certification: ${JSON.stringify((metadata as IssueMetadata).certification)}`);
    }
  }

  if (isCorsiaDefault(metadata)) {
    return parseFloat(calculationsCorsia.default.equations[sustainabilityTier](metadata).toFixed(unitVolumeFactor));
  } else if (isCorsiaActual(metadata)) {
    return parseFloat(calculationsCorsia.actual.equations[sustainabilityTier](metadata).toFixed(unitVolumeFactor));
  } else if (isEuActual(metadata)) {
    return parseFloat(calculationsEu.actual.equations[sustainabilityTier](metadata).toFixed(unitVolumeFactor));
  } else if (isEuDefault(metadata)) {
    return parseFloat(calculationsEu.default.equations[sustainabilityTier](metadata).toFixed(unitVolumeFactor));
  } else {
    const _: never = metadata;
    throw new Error(`Unknown certification: ${JSON.stringify((metadata as IssueMetadata).certification)}`);
  }
};

export const calculateTonnesAbated = (metadata: IssueMetadata, volume: number, baselineValue: number, carbonIntensity: number): number => {
  return (((baselineValue - carbonIntensity) / 1000000) * (metadata['product'].lowerHeatingValue * 1000 * volume));
};

export const calculateSustainabilityTierType = (susTier: SustainabilityTier) => {
  return SustainabilityTierTypes[susTier];
};

export const calculateUsabilityTierInfoForSAFC = (usabilityTier: UsabilityTier | 0): string => {
  if (usabilityTier === 0) {
    return '';
  }
  return UsabilityTierInfoForSAFC[usabilityTier];
};

export const calculateGreenhouseGasReduction = (baselineFuelEmissions: number, carbonIntensity: number): number => {
  return Number((((baselineFuelEmissions - carbonIntensity) / baselineFuelEmissions) * 100).toFixed(unitVolumeFactor));
};
